import { useMemo } from 'react';
import { useGqlQuery } from '@API/useAvQuery';
import { QueryKey } from '@fabric-ui/utils';
import { GET_MEASUREMENTS_AND_CALCULATED_DIMENSIONS } from './gql';
import { getType } from './useEntities';

export type CalcDimType = { kindWrapper?: { kind?: string }; typeNameWrapper?: { name?: string } } | string | undefined;

// Unwraps a raw calc dim type into either its protobuf field kind (kindWrapper) or its type name (typeNameWrapper, e.g. timestamp)
export const extractCalcDimTypeKind = (type: CalcDimType): { kind: string } | { typeName: string } | undefined => {
  if (type && typeof type === 'object') {
    if (type.kindWrapper?.kind) {
      return { kind: type.kindWrapper.kind };
    }
    if (type.typeNameWrapper?.name) {
      return { typeName: type.typeNameWrapper.name };
    }
    return undefined;
  }
  return typeof type === 'string' ? { kind: type } : undefined;
};

export const parseCalcDimType = (type: CalcDimType): string | undefined => {
  const unwrapped = extractCalcDimTypeKind(type);
  if (!unwrapped) {
    return undefined;
  }
  return 'typeName' in unwrapped ? getType({ name: unwrapped.typeName }, false) : unwrapped.kind;
};

export const useCalcDims = (options?) => {
  const queryResult = useGqlQuery<{
    findMeasurementsByAccountId: { measurement; createdByUserId; updatedByUserId; id }[];
    findCalculatedDimensionsByAccountId: { calculatedDimension; createdByUserId; updatedByUserId; id }[];
  }>({
    queryKey: [QueryKey.MeasurementsAndCalculatedDimensions],
    query: GET_MEASUREMENTS_AND_CALCULATED_DIMENSIONS,
    select: (_, data) => data,
    ...options,
  });

  const data = useMemo(() => {
    if (!queryResult.data) {
      return { all: [], visible: [], asObject: {} };
    }

    const calculatedDimensionsData = queryResult.data.findCalculatedDimensionsByAccountId || [];

    const calculatedDimensionsAll = calculatedDimensionsData.map(v => {
      const { calculatedDimension, ...rest } = v;
      return {
        ...calculatedDimension,
        ...rest,
        // Handle the nested type structure for calculated dimensions
        type: parseCalcDimType(calculatedDimension.type),
        builtin: calculatedDimension.builtin ?? false,
        hidden: calculatedDimension.hidden ?? false,
        createdByUserId: v.createdByUserId,
        updatedByUserId: v.updatedByUserId,
        id: v.id,
      };
    });

    return {
      all: calculatedDimensionsAll,
      visible: calculatedDimensionsAll.filter(({ hidden }) => !hidden),
      asObject: calculatedDimensionsAll.reduce((acc, item) => {
        const { systemName, ...rest } = item;
        acc[systemName] = rest;
        return acc;
      }, {}),
    };
  }, [queryResult.data]);

  return { ...queryResult, data };
};
